/**
 * Planner — two tabs. Week ahead: work blocks (planner/WeekPlanner.tsx).
 * Revenue & capacity: revenue and capacity, month by month, from this month
 * forward.
 *
 * A rolling window over standalone plan entries (see lib/planner.ts). The grid
 * is computed on every render from the entries and settings; nothing on this
 * page is a stored figure. Nothing here reads or writes Project Hub's projects,
 * clients or time either — rates are typed in, so modelling a bid can never
 * touch what is actually billed.
 *
 * No past months: this is for deciding what to take on, not for reviewing what
 * happened. The window starts at the current month and can only move forward.
 */

import { Fragment, useMemo, useState } from "react";

import { card, ProgressBar } from "@/components/atoms";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useChrome } from "@/context/ChromeContext";
import { HeaderTabs } from "@/components/HeaderTabs";
import { useToast } from "@/context/ToastContext";
import { EMPTY_SETTINGS, usePlanEntries, usePlanMutations, usePlanSettings } from "@/hooks/usePlanner";
import {
  addMonths, anchorSegments, autoSort, cellFor, currentMonth, earnedRateOver, goalFor, manualSort,
  monthLabel, monthsBetween, monthTotals, monthYearLabel, moveItem, normalizeSegments, projectTotal,
  removeSegmentAt, segmentFor, setMonthOverride, WPM, yearMonths, yearOf, yearTotals,
  type Dated, type MonthKey, type PlanEntry, type PlanKind, type PlanSettings, type PlanStatus, type Segment,
} from "@/lib/planner";
import { money } from "@/lib/timeRollup";

import { WeekPlanner } from "./planner/WeekPlanner";
import { arrowBtn, ghost, iconBtn, input, label, linkBtn, loadPref, Modal, on, primary, rowInput, savePref, Seg } from "./planner/parts";

type View = "revenue" | "hours" | "both";
type SortMode = "auto" | "manual";

const KIND_LABEL: Record<PlanKind, string> = { retainer: "Retainer", fixed: "Fixed fee", hourly: "Hourly" };
const STATUS_TONE: Record<PlanStatus, { bg: string; ink: string; bar: string }> = {
  confirmed: { bg: "var(--goodTint)", ink: "var(--goodInk)", bar: "var(--good)" },
  potential: { bg: "var(--warnTint)", ink: "var(--warn)", bar: "var(--warn)" },
};
const HORIZONS = [12, 18, 24, 36];

const hrs = (n: number) => `${Math.round(n * 10) / 10}`;

type Mode = "week" | "year";

/**
 * Two views under one top-bar item, because both answer "do I have the hours
 * for this?": Week ahead plans the coming week's work blocks onto Google
 * Calendar (views/planner/WeekPlanner.tsx); Revenue & capacity is the month
 * grid below. The keys stay "week"/"year" so a remembered tab survives.
 * It reopens on whichever you used last.
 */
export function Planner() {
  const [mode, setMode] = useState<Mode>(() => loadPref<Mode>("ph.planner.mode", "week"));
  const tabs = (
    <HeaderTabs value={mode} options={[["week", "Week ahead"], ["year", "Revenue & capacity"]]}
      onChange={(key) => { setMode(key); savePref("ph.planner.mode", key); }} />
  );
  return mode === "week" ? <WeekPlanner tabs={tabs} /> : <YearPlanner tabs={tabs} />;
}

function YearPlanner({ tabs }: { tabs: React.ReactNode }) {
  const toast = useToast();
  const { data: entries = [] } = usePlanEntries();
  const { data: storedSettings } = usePlanSettings();
  const settings: PlanSettings = storedSettings ?? EMPTY_SETTINGS;
  const { saveEntry, deleteEntry, reorder, saveSettings } = usePlanMutations();

  const today = currentMonth();
  const [start, setStart] = useState<MonthKey>(today);
  const [horizon, setHorizon] = useState(() => Number(loadPref("ph.planner.horizon", "18")) || 18);
  const [view, setView] = useState<View>(() => loadPref<View>("ph.planner.view", "revenue"));
  const [editing, setEditing] = useState<PlanEntry | null>(null);
  const [cell, setCell] = useState<{ entry: PlanEntry; month: MonthKey } | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>(() => loadPref<SortMode>("ph.planner.sort", "auto"));

  // Auto: confirmed on top, potential below, each by start month. Manual: the
  // order you set with the arrows, kept in `position`.
  const ordered = useMemo(() => (sortMode === "auto" ? autoSort(entries) : manualSort(entries)), [entries, sortMode]);
  const firstPotential = sortMode === "auto" && ordered.some((e) => e.status === "confirmed")
    ? ordered.findIndex((e) => e.status === "potential")
    : -1;

  function move(index: number, dir: -1 | 1) {
    const next = moveItem(ordered, index, dir);
    if (next !== ordered) reorder.mutate(next, { onError: () => toast("Could not save the new order") });
  }

  const months = useMemo(() => monthsBetween(start, addMonths(start, horizon - 1)), [start, horizon]);
  const totals = useMemo(() => monthTotals(entries, months, settings.capacity), [entries, months, settings.capacity]);
  // Annual totals cover each calendar year the window touches in full — not
  // just the months on screen — from this month on: the planner has no past.
  const annual = useMemo(
    () => [...new Set(months.map(yearOf))].map((year) => ({ year, months: yearMonths(year, today) })),
    [months, today],
  );
  const years = useMemo(
    () => yearTotals(monthTotals(entries, annual.flatMap((a) => a.months)), settings.goals),
    [entries, annual, settings.goals],
  );
  const cols = months.length + 2 + annual.length;
  const hasGoal = years.some((y) => y.goal > 0);
  const hasCapacity = settings.capacity.length > 0;

  // Year header spans: consecutive months of the same year.
  const yearSpans = useMemo(() => {
    const out: { year: string; span: number }[] = [];
    for (const m of months) {
      const y = yearOf(m);
      const last = out[out.length - 1];
      if (last?.year === y) last.span += 1;
      else out.push({ year: y, span: 1 });
    }
    return out;
  }, [months]);

  const shift = (n: number) => setStart((s) => {
    const next = addMonths(s, n);
    return next < today ? today : next;
  });

  async function persist(entry: PlanEntry, message?: string) {
    const { id, ...rest } = anchorSegments({
      ...entry,
      // A change dated after the entry ends can never apply; drop it rather
      // than carry an invisible segment around.
      segments: normalizeSegments(entry.segments.filter((s) => s.from <= entry.end)),
    });
    try {
      await saveEntry.mutateAsync(id ? { id, ...rest } : rest);
      if (message) toast(message);
      return true;
    } catch {
      toast("Could not save that entry");
      return false;
    }
  }

  async function duplicate(entry: PlanEntry) {
    const { id: _id, ...rest } = entry;
    const ok = await persist({ ...rest, id: "", name: `${entry.name} (copy)`, status: "potential", position: entries.length } as PlanEntry, "Duplicated as a potential entry");
    if (ok) setEditing(null);
  }

  useChrome({
    title: "Planner",
    subtitle: "Revenue and capacity from this month forward",
    tabs,
    actions: (
      <span style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setShowSettings((v) => !v)} style={{ ...ghost, ...(showSettings ? on : null) }}>Capacity & goals</button>
        <button onClick={() => setEditing(blankEntry(today, entries.length))} style={primary}>+ Add entry</button>
      </span>
    ),
  }, [showSettings, entries.length]);

  return (
    <div style={{ padding: "20px 36px 90px", display: "flex", flexDirection: "column", gap: 16 }}>
      {showSettings && (
        <SettingsPanel
          settings={settings}
          years={[...new Set(months.map(yearOf))]}
          saving={saveSettings.isPending}
          onSave={async (next) => {
            try { await saveSettings.mutateAsync(next); toast("Capacity and goals saved"); setShowSettings(false); }
            catch { toast("Could not save capacity and goals"); }
          }}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* -- Window controls ---------------------------------------------------- */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => shift(-12)} disabled={start <= today} aria-label="Back a year" style={pagerStyle(start <= today)}>«</button>
        <button onClick={() => shift(-1)} disabled={start <= today} aria-label="Back a month" style={pagerStyle(start <= today)}>‹</button>
        <div className="mono" style={{
          height: 36, minWidth: 190, padding: "0 12px", borderRadius: 8, fontSize: 13,
          display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line2)",
        }}>{monthYearLabel(months[0])} – {monthYearLabel(months[months.length - 1])}</div>
        <button onClick={() => shift(1)} aria-label="Forward a month" style={pagerStyle(false)}>›</button>
        <button onClick={() => shift(12)} aria-label="Forward a year" style={pagerStyle(false)}>»</button>
        {start !== today && <button onClick={() => setStart(today)} style={ghost}>This month</button>}

        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={horizon}
            onChange={(e) => { setHorizon(Number(e.target.value)); savePref("ph.planner.horizon", e.target.value); }}
            aria-label="Months shown"
            style={{ ...ghost, paddingRight: 8 }}
          >
            {HORIZONS.map((h) => <option key={h} value={h}>{h} months</option>)}
          </select>
          <Seg value={sortMode} onChange={(v) => { setSortMode(v); savePref("ph.planner.sort", v); }}
            options={[["auto", "Auto sort"], ["manual", "Manual"]]} />
          <Seg value={view} onChange={(v) => { setView(v); savePref("ph.planner.view", v); }}
            options={[["revenue", "Revenue"], ["hours", "Hours"], ["both", "Both"]]} />
        </span>
      </div>

      {/* -- Grid --------------------------------------------------------------- */}
      <div style={{ ...card, overflowX: "auto", padding: 0 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12.5, minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ ...stickyCol, ...th, borderBottom: "none" }} />
              {yearSpans.map((y) => (
                <th key={y.year} colSpan={y.span} style={{ ...th, textAlign: "left", borderLeft: "1px solid var(--line)", borderBottom: "none", color: "var(--ink2)" }}>
                  {y.year}
                </th>
              ))}
              <th style={{ ...th, borderBottom: "none" }} />
              <th colSpan={annual.length} title="Each calendar year in full, from this month on" style={{ ...th, ...yearCol, textAlign: "left", borderBottom: "none", color: "var(--ink2)" }}>
                Annual
              </th>
            </tr>
            <tr>
              <th style={{ ...stickyCol, ...th, textAlign: "left" }}>Entry</th>
              {months.map((m) => (
                <th key={m} style={{ ...th, borderLeft: m.endsWith("-01") ? "1px solid var(--line)" : undefined }}>{monthLabel(m)}</th>
              ))}
              <th style={{ ...th, textAlign: "right", paddingRight: 14 }}>Total</th>
              {annual.map((a, i) => (
                <th key={a.year} title={a.months.length === 12 ? `${a.year}, full year` : `${a.year}, ${monthLabel(a.months[0])}–Dec`}
                  style={{ ...th, ...(i === 0 ? yearCol : null), textAlign: "right", paddingRight: 14 }}>
                  {a.year}{a.months.length < 12 && <span style={{ fontWeight: 400, color: "var(--muted2)" }}> {monthLabel(a.months[0])}–Dec</span>}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={cols} style={{ padding: "36px 16px", textAlign: "center", color: "var(--muted)" }}>
                Nothing planned yet. Add a retainer, a fixed-fee job or an hourly engagement — confirmed or potential.
              </td></tr>
            )}
            {ordered.map((p, index) => {
              const tone = STATUS_TONE[p.status];
              const rate = p.kind === "hourly" ? segmentFor(p, months.find((m) => m >= p.start && m <= p.end) ?? p.start)?.hourlyRate : earnedRateOver(p, months);
              return (
                <Fragment key={p.id}>
                {index === firstPotential && (
                  <tr>
                    <td colSpan={cols} style={{ ...td, padding: "6px 10px", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted2)", background: "var(--surface2)" }}>
                      <span style={{ position: "sticky", left: 10 }}>Potential</span>
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ ...stickyCol, ...td, borderLeft: `3px solid ${tone.bar}`, cursor: "pointer", minWidth: 230 }} onClick={() => setEditing(p)} title="Edit entry">
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--ink)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{p.name}</div>
                    {sortMode === "manual" && (
                      <span style={{ display: "flex", gap: 2, flex: "none" }} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${p.name} up`} style={arrowBtn(index === 0)}>▲</button>
                        <button onClick={() => move(index, 1)} disabled={index === ordered.length - 1} aria-label={`Move ${p.name} down`} style={arrowBtn(index === ordered.length - 1)}>▼</button>
                      </span>
                    )}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 3, fontSize: 11.5, color: "var(--muted)", whiteSpace: "nowrap" }}>
                      <span style={{ padding: "1px 7px", borderRadius: 999, background: tone.bg, color: tone.ink }}>{p.status === "confirmed" ? "Confirmed" : "Potential"}</span>
                      <span>{KIND_LABEL[p.kind]}</span>
                      {rate ? <span className="mono" title={p.kind === "hourly" ? "Hourly rate" : "What the planned hours earn per hour across this window"}>
                        {money(rate)}/h{p.kind === "hourly" ? "" : " earned"}
                      </span> : null}
                    </div>
                  </td>
                  {months.map((m) => {
                    const inRange = m >= p.start && m <= p.end;
                    const c = cellFor(p, m);
                    const changes = inRange && m !== p.start && p.segments.some((s) => s.from === m);
                    return (
                      <td
                        key={m}
                        onClick={inRange ? () => setCell({ entry: p, month: m }) : undefined}
                        className={inRange ? "row-hover" : undefined}
                        title={inRange ? "Change this month" : undefined}
                        style={{
                          ...td, ...numCell, position: "relative",
                          cursor: inRange ? "pointer" : "default",
                          background: inRange ? undefined : "var(--surface2)",
                          borderLeft: m.endsWith("-01") ? "1px solid var(--line)" : undefined,
                        }}
                      >
                        {changes && <span title="Values change this month" style={{ position: "absolute", top: 5, left: 5, width: 5, height: 5, borderRadius: "50%", background: "var(--accent)" }} />}
                        {inRange && <CellValue revenue={c.revenue} hours={c.hoursPerWeek} view={view} />}
                      </td>
                    );
                  })}
                  <td style={{ ...td, ...numCell, fontWeight: 600, paddingRight: 14 }}>{money(projectTotal(p, months))}</td>
                  {annual.map((a, i) => {
                    const v = projectTotal(p, a.months);
                    return (
                      <td key={a.year} style={{ ...td, ...numCell, ...(i === 0 ? yearCol : null), paddingRight: 14, color: v ? "var(--ink2)" : "var(--muted3)" }}>
                        {v ? money(v) : "—"}
                      </td>
                    );
                  })}
                </tr>
                </Fragment>
              );
            })}
          </tbody>

          <tfoot>
            <SummaryRow label="Confirmed revenue" cells={totals.map((t) => money(t.confirmedRev))} total={money(sum(totals, "confirmedRev"))}
              annual={years.map((y) => (y.confirmedRev ? money(y.confirmedRev) : "—"))} first />
            <SummaryRow label="With potential" cells={totals.map((t) => money(t.allRev))} total={money(sum(totals, "allRev"))}
              annual={years.map((y) => (y.allRev ? money(y.allRev) : "—"))} muted />
            {hasGoal && (
              <SummaryRow label="Goal pace" hint="Annual goal ÷ 12"
                cells={months.map((m) => { const g = goalFor(settings.goals, yearOf(m)); return g ? money(g / 12) : "—"; })}
                total="" annual={years.map((y) => (y.goal ? money(y.goal * (y.months / 12)) : "—"))} muted />
            )}
            <SummaryRow label="Booked h/wk" cells={totals.map((t) => hrs(t.confirmedHrs))} total="" annual={annual.map(() => "")} first />
            <SummaryRow label="With potential h/wk" cells={totals.map((t) => hrs(t.allHrs))} total="" annual={annual.map(() => "")} muted />
            {hasCapacity && (
              <>
                <SummaryRow label="Capacity h/wk" cells={totals.map((t) => hrs(t.capacity))} total="" annual={annual.map(() => "")} muted />
                <SummaryRow
                  label="Headroom h/wk"
                  hint="Capacity minus confirmed and potential work"
                  cells={totals.map((t) => {
                    const left = t.capacity - t.allHrs;
                    // Red when confirmed work alone overbooks you; amber when
                    // only winning the potential work would.
                    const color = t.capacity - t.confirmedHrs < 0 ? "var(--bad)" : left < 0 ? "var(--warn)" : "var(--goodInk)";
                    return <span style={{ color, fontWeight: 600 }}>{left > 0 ? "+" : ""}{hrs(left)}</span>;
                  })}
                  total="" annual={annual.map(() => "")}
                />
              </>
            )}
          </tfoot>
        </table>
      </div>

      {!hasCapacity && (
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
          Set your capacity under <button onClick={() => setShowSettings(true)} style={linkBtn}>Capacity & goals</button> to see headroom and overbooked months.
        </div>
      )}

      {/* -- Years -------------------------------------------------------------- */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
        {years.map((y) => {
          // This year is only the months from now on, so it is compared with the
          // same share of its goal — the planner knows nothing about the past.
          const goal = y.goal * (y.months / 12);
          return (
            <div key={y.year} style={{ ...card, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>{y.year}</span>
                <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
                  {y.months === 12 ? "full year" : `rest of year, ${y.months} ${y.months === 1 ? "month" : "months"}`}
                </span>
              </div>
              <Figure label="Confirmed" value={money(y.confirmedRev)} />
              <Figure label="With potential" value={money(y.allRev)} muted />
              {goal > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--muted)", marginBottom: 5 }}>
                    <span>Goal{y.months < 12 ? ` for the rest of the year` : ""} {money(goal)}</span>
                    <span className="mono">{Math.round((y.confirmedRev / goal) * 100)}% · {Math.round((y.allRev / goal) * 100)}%</span>
                  </div>
                  <div style={{ position: "relative" }}>
                    <ProgressBar value={Math.min(100, (y.allRev / goal) * 100)} color="var(--warn)" height={6} />
                    <div style={{ position: "absolute", inset: 0 }}>
                      <ProgressBar value={Math.min(100, (y.confirmedRev / goal) * 100)} color="var(--good)" height={6} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <EntryEditor
          entry={editing}
          saving={saveEntry.isPending}
          onCancel={() => setEditing(null)}
          onSave={async (next) => { if (await persist(next, next.id ? "Entry saved" : "Entry added")) setEditing(null); }}
          onDuplicate={editing.id ? () => void duplicate(editing) : undefined}
          onDelete={editing.id ? async () => {
            try { await deleteEntry.mutateAsync(editing.id); toast("Entry deleted"); setEditing(null); }
            catch { toast("Could not delete that entry"); }
          } : undefined}
        />
      )}

      {cell && (
        <CellEditor
          entry={cell.entry}
          month={cell.month}
          onCancel={() => setCell(null)}
          onSave={async (next) => { if (await persist(next)) setCell(null); }}
        />
      )}
    </div>
  );
}

function sum(rows: ReturnType<typeof monthTotals>, key: "confirmedRev" | "allRev"): number {
  return rows.reduce((n, r) => n + r[key], 0);
}

function blankEntry(month: MonthKey, position: number): PlanEntry {
  return {
    id: "", name: "", kind: "retainer", status: "confirmed",
    start: month, end: addMonths(month, 11), position,
    segments: [{ from: month }],
  };
}

/* -- Grid parts ------------------------------------------------------------- */

function CellValue({ revenue, hours, view }: { revenue: number; hours: number; view: View }) {
  const rev = <span className="mono" style={{ color: revenue ? "var(--ink)" : "var(--muted3)" }}>{revenue ? money(revenue) : "—"}</span>;
  const h = <span className="mono" style={{ color: "var(--muted)", fontSize: view === "both" ? 11 : 12.5 }}>{hours ? `${hrs(hours)} h/wk` : "—"}</span>;
  if (view === "revenue") return rev;
  if (view === "hours") return h;
  return <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>{rev}{h}</span>;
}

function SummaryRow({ label, hint, cells, total, annual, muted, first }: {
  label: string; hint?: string; cells: React.ReactNode[]; total: string; annual: string[]; muted?: boolean; first?: boolean;
}) {
  const border = first ? "2px solid var(--line)" : "1px solid var(--tint2)";
  return (
    <tr>
      <td title={hint} style={{ ...stickyCol, ...td, borderTop: border, fontSize: 12.5, fontWeight: muted ? 400 : 600, color: muted ? "var(--ink3)" : "var(--ink)" }}>{label}</td>
      {cells.map((c, i) => (
        <td key={i} className="mono" style={{ ...td, ...numCell, borderTop: border, color: muted ? "var(--ink3)" : "var(--ink)", fontWeight: muted ? 400 : 600 }}>{c}</td>
      ))}
      <td className="mono" style={{ ...td, ...numCell, borderTop: border, fontWeight: 600, paddingRight: 14 }}>{total}</td>
      {annual.map((a, i) => (
        <td key={i} className="mono" style={{ ...td, ...numCell, ...(i === 0 ? yearCol : null), borderTop: border, paddingRight: 14, color: muted ? "var(--ink3)" : "var(--ink)", fontWeight: muted ? 400 : 600 }}>{a}</td>
      ))}
    </tr>
  );
}

function Figure({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 13 }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span className="mono" style={{ fontWeight: 600, color: muted ? "var(--ink3)" : "var(--ink)" }}>{value}</span>
    </div>
  );
}

/* -- Editors ---------------------------------------------------------------- */

/** The number fields an entry's kind uses. */
function fieldsFor(kind: PlanKind): { key: keyof Segment; label: string; prefix?: string }[] {
  return kind === "hourly"
    ? [{ key: "hourlyRate", label: "Rate $/h", prefix: "$" }, { key: "hoursPerWeek", label: "Hours/week" }]
    : [{ key: "monthlyAmount", label: "Amount $/mo", prefix: "$" }, { key: "hoursPerWeek", label: "Hours/week" }];
}

const numOrUndef = (v: string): number | undefined => {
  const n = Number(v.replace(/[$,\s]/g, ""));
  return v.trim() === "" || !Number.isFinite(n) ? undefined : n;
};

function EntryEditor({ entry, saving, onSave, onCancel, onDelete, onDuplicate }: {
  entry: PlanEntry; saving: boolean;
  onSave: (e: PlanEntry) => void; onCancel: () => void;
  onDelete?: () => void; onDuplicate?: () => void;
}) {
  const [draft, setDraft] = useState<PlanEntry>(entry);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (patch: Partial<PlanEntry>) => { setDraft((d) => ({ ...d, ...patch })); setError(null); };
  const setSeg = (i: number, patch: Partial<Segment>) =>
    set({ segments: draft.segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const fields = fieldsFor(draft.kind);

  function submit() {
    if (!draft.name.trim()) { setError("Give it a name"); return; }
    if (!draft.start || !draft.end) { setError("Set a start and end month"); return; }
    if (draft.end < draft.start) { setError("The end month is before the start"); return; }
    onSave({ ...draft, name: draft.name.trim() });
  }

  return (
    <Modal title={entry.id ? "Edit entry" : "New entry"} onClose={onCancel} width={560}>
      <label style={label}>Name
        <input value={draft.name} onChange={(e) => set({ name: e.target.value })} style={input} autoFocus placeholder="e.g. Riverside strategy retainer" />
      </label>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={label}>Kind
          <div style={{ marginTop: 4 }}><Seg value={draft.kind} onChange={(k) => set({ kind: k })} options={[["retainer", "Retainer"], ["fixed", "Fixed fee"], ["hourly", "Hourly"]]} /></div>
        </div>
        <div style={label}>Status
          <div style={{ marginTop: 4 }}><Seg value={draft.status} onChange={(s) => set({ status: s })} options={[["confirmed", "Confirmed"], ["potential", "Potential"]]} /></div>
        </div>
      </div>

      {/* Wraps to one column when the dialog is narrow: a month input has a
          wide intrinsic size, so two at 1fr each overlapped on a small screen. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", columnGap: 12 }}>
        <label style={label}>First month
          <input type="month" value={draft.start} onChange={(e) => set({ start: e.target.value })} style={input} />
        </label>
        <label style={label}>Last month
          <input type="month" value={draft.end} onChange={(e) => set({ end: e.target.value })} style={input} />
        </label>
      </div>

      <div style={{ ...label, marginBottom: 6 }}>
        {draft.kind === "fixed"
          ? "Amounts — set the usual month here, then click any month in the grid to change just that month."
          : "Rates and hours — add a change for a raise or a rescope from a given month."}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: SEG_COLS, gap: 8, fontSize: 11, color: "var(--muted2)" }}>
          <span>From</span>{fields.map((f) => <span key={f.key}>{f.label}</span>)}<span />
        </div>
        {draft.segments.map((s, i) => (
          <Fragment key={i}>
          <div style={{ display: "grid", gridTemplateColumns: SEG_COLS, gap: 8, alignItems: "center" }}>
            {i === 0
              ? <span style={{ fontSize: 12, color: "var(--muted)" }}>From the start</span>
              : <input type="month" value={s.from} onChange={(e) => setSeg(i, { from: e.target.value })} aria-label="From month" style={rowInput} />}
            {fields.map((f) => (
              <input
                key={f.key}
                inputMode="decimal"
                value={s[f.key] ?? ""}
                onChange={(e) => setSeg(i, { [f.key]: numOrUndef(e.target.value) })}
                placeholder={f.label}
                aria-label={f.label}
                style={rowInput}
              />
            ))}
            {i > 0
              ? <button onClick={() => set({ segments: draft.segments.filter((_, j) => j !== i) })} aria-label="Remove this change" style={iconBtn}>✕</button>
              : <span />}
          </div>
          <SegmentEstimate entry={draft} index={i} />
          </Fragment>
        ))}
        <button
          onClick={() => {
            const last = draft.segments[draft.segments.length - 1];
            const from = addMonths(last.from < draft.start ? draft.start : last.from, 1);
            set({ segments: [...draft.segments, { ...last, from }] });
          }}
          style={{ ...linkBtn, alignSelf: "flex-start", fontSize: 12.5 }}
        >+ Add a change from a later month</button>
      </div>

      <EntryEstimate entry={draft} />

      {error && <div style={{ fontSize: 12, color: "var(--bad)", marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        {onDelete && <button onClick={() => setConfirmDelete(true)} style={{ ...ghost, color: "var(--bad)", marginRight: onDuplicate ? 0 : "auto" }}>Delete</button>}
        {onDuplicate && <button onClick={onDuplicate} style={{ ...ghost, marginRight: "auto" }}>Duplicate</button>}
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={submit} disabled={saving} style={primary}>{saving ? "Saving…" : "Save"}</button>
      </div>

      {confirmDelete && onDelete && (
        <ConfirmDialog
          title="Delete this entry?"
          message={`“${entry.name}” and its scheduled changes will be removed from the planner. Nothing else in Project Hub is affected.`}
          onConfirm={() => { setConfirmDelete(false); onDelete(); }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}

/**
 * The months one segment of a draft covers: from its own month (the first
 * segment from the start) up to the month before the next change, or the end.
 * Computed from the draft as typed, so it is right before anything is saved.
 */
function segmentSpan(entry: PlanEntry, index: number): MonthKey[] {
  if (!entry.start || !entry.end || entry.end < entry.start) return [];
  const from = index === 0 ? entry.start : entry.segments[index].from;
  if (!from || from > entry.end) return [];
  const later = entry.segments
    .map((s, j) => (j === 0 ? entry.start : s.from))
    .filter((f, j) => j !== index && f > from)
    .sort();
  const until = later.length ? addMonths(later[0], -1) : entry.end;
  return monthsBetween(from < entry.start ? entry.start : from, until > entry.end ? entry.end : until);
}

/** One line under each row: what that rate works out to, and for how long. */
function SegmentEstimate({ entry, index }: { entry: PlanEntry; index: number }) {
  const s = entry.segments[index];
  const h = s.hoursPerWeek ?? 0;
  const span = segmentSpan(entry, index);
  const perMonth = entry.kind === "hourly" ? h * WPM * (s.hourlyRate ?? 0) : (s.monthlyAmount ?? 0);
  if (!perMonth && !h) return null;

  const parts: string[] = [];
  if (entry.kind === "hourly") {
    if (perMonth) parts.push(`≈ ${money(perMonth)}/mo  (${hrs(h)} h/wk × 4.33 wk × ${money(s.hourlyRate ?? 0)})`);
  } else if (perMonth && h) {
    parts.push(`≈ ${money(perMonth / (h * WPM))}/h earned  (${money(perMonth)} ÷ ${hrs(h * WPM)} h/mo)`);
  }
  if (h) parts.push(`${hrs(h * WPM)} h/mo`);
  if (span.length && perMonth) parts.push(`${span.length} ${span.length === 1 ? "month" : "months"} = ${money(perMonth * span.length)}`);

  return (
    <div className="mono" style={{ fontSize: 11.5, color: "var(--muted)", margin: "-2px 0 4px", paddingLeft: 2 }}>
      {parts.join("  ·  ")}
    </div>
  );
}

/** The whole engagement, as it would be saved. */
function EntryEstimate({ entry }: { entry: PlanEntry }) {
  if (!entry.start || !entry.end || entry.end < entry.start) return null;
  const months = monthsBetween(entry.start, entry.end);
  const saved = anchorSegments({ ...entry, segments: normalizeSegments(entry.segments.filter((s) => s.from && s.from <= entry.end)) });
  const total = projectTotal(saved, months);
  const hours = months.reduce((n, m) => n + cellFor(saved, m).hoursPerWeek * WPM, 0);
  if (!total && !hours) return null;

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10,
      padding: "11px 13px", marginBottom: 14, borderRadius: 10,
      background: "var(--accentTint2)", border: "1px solid var(--accentLine)",
    }}>
      <Estimate label="Total" value={money(total)} strong />
      <Estimate label={`Avg / month (${months.length})`} value={money(total / months.length)} />
      <Estimate label="Hours total" value={`${Math.round(hours)} h`} />
      {hours > 0 && total > 0 && <Estimate label="Earned / hour" value={money(total / hours)} />}
    </div>
  );
}

function Estimate({ label: l, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted)" }}>{l}</div>
      <div className="mono" style={{ fontSize: strong ? 16 : 14, fontWeight: strong ? 700 : 600, color: "var(--ink)", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function CellEditor({ entry, month, onSave, onCancel }: {
  entry: PlanEntry; month: MonthKey; onSave: (e: PlanEntry) => void; onCancel: () => void;
}) {
  const current = segmentFor(entry, month) ?? { from: month };
  const fields = fieldsFor(entry.kind);
  const [values, setValues] = useState<Partial<Segment>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, current[f.key]])));
  const [scope, setScope] = useState<"month" | "onward">("month");
  const startsHere = month !== entry.start && entry.segments.some((s) => s.from === month);
  const preview = cellFor(setMonthOverride(entry, month, values, scope), month);

  return (
    <Modal title={`${entry.name} · ${monthYearLabel(month)}`} onClose={onCancel} width={420}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {fields.map((f) => (
          <label key={f.key} style={label}>{f.label}
            <input
              inputMode="decimal"
              value={values[f.key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: numOrUndef(e.target.value) }))}
              style={input}
              autoFocus={f.key !== "hoursPerWeek"}
            />
          </label>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "2px 0 14px", fontSize: 13, color: "var(--ink2)" }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="radio" checked={scope === "month"} onChange={() => setScope("month")} />
          Just {monthYearLabel(month)}
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="radio" checked={scope === "onward"} onChange={() => setScope("onward")} />
          {monthYearLabel(month)} onward, until the next scheduled change
        </label>
      </div>

      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>
        This month: <span className="mono" style={{ color: "var(--ink)" }}>{money(preview.revenue)}</span> at {hrs(preview.hoursPerWeek)} h/wk
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {startsHere && (
          <button onClick={() => onSave(removeSegmentAt(entry, month))} style={{ ...ghost, marginRight: "auto" }}>
            Remove this month's change
          </button>
        )}
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={() => onSave(setMonthOverride(entry, month, values, scope))} style={primary}>Save</button>
      </div>
    </Modal>
  );
}

function SettingsPanel({ settings, years, saving, onSave, onClose }: {
  settings: PlanSettings; years: string[]; saving: boolean;
  onSave: (s: PlanSettings) => void; onClose: () => void;
}) {
  const [capacity, setCapacity] = useState<Dated[]>(() =>
    settings.capacity.length ? [...settings.capacity].sort((a, b) => a.from.localeCompare(b.from)) : [{ from: currentMonth(), value: 0 }]);
  const [goals, setGoals] = useState<Record<string, number>>(settings.goals);
  const setCap = (i: number, patch: Partial<Dated>) => setCapacity((c) => c.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  return (
    <div style={{ ...card, padding: "16px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
      <div>
        <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Capacity</div>
        <div style={{ fontSize: 12, color: "var(--muted)", margin: "3px 0 10px" }}>
          Hours a week you can give to client work. Change it from a given month if that changes.
        </div>
        {capacity.map((d, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "140px 110px 28px", gap: 8, alignItems: "center", marginBottom: 6 }}>
            {i === 0
              ? <span style={{ fontSize: 12, color: "var(--muted)" }}>From now</span>
              : <input type="month" value={d.from} onChange={(e) => setCap(i, { from: e.target.value })} aria-label="From month" style={rowInput} />}
            <input inputMode="decimal" value={d.value || ""} onChange={(e) => setCap(i, { value: numOrUndef(e.target.value) ?? 0 })} placeholder="h/week" aria-label="Hours per week" style={rowInput} />
            {i > 0 ? <button onClick={() => setCapacity((c) => c.filter((_, j) => j !== i))} aria-label="Remove" style={iconBtn}>✕</button> : <span />}
          </div>
        ))}
        <button onClick={() => setCapacity((c) => [...c, { from: addMonths(c[c.length - 1].from, 1), value: c[c.length - 1].value }])} style={{ ...linkBtn, fontSize: 12.5 }}>
          + Change from a later month
        </button>
      </div>

      <div>
        <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Revenue goals</div>
        <div style={{ fontSize: 12, color: "var(--muted)", margin: "3px 0 10px" }}>
          A year left blank carries the previous year's goal forward.
        </div>
        {years.map((y) => {
          const inherited = goalFor({ ...goals, [y]: 0 }, y);
          return (
            <div key={y} style={{ display: "grid", gridTemplateColumns: "60px 160px", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13 }}>{y}</span>
              <input
                inputMode="decimal"
                value={goals[y] ? String(goals[y]) : ""}
                onChange={(e) => setGoals((g) => ({ ...g, [y]: numOrUndef(e.target.value) ?? 0 }))}
                placeholder={inherited ? `${money(inherited)} (carried)` : "Annual goal $"}
                aria-label={`${y} revenue goal`}
                style={rowInput}
              />
            </div>
          );
        })}
      </div>

      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={ghost}>Cancel</button>
        <button
          disabled={saving}
          onClick={() => onSave({
            // The first capacity row is "from now"; store it from the earliest
            // month it could matter so it never falls out of the window.
            capacity: capacity.filter((d) => d.value > 0).map((d, i) => (i === 0 ? { ...d, from: d.from < currentMonth() ? d.from : currentMonth() } : d)),
            goals: Object.fromEntries(Object.entries(goals).filter(([, v]) => v > 0)),
          })}
          style={primary}
        >{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

/* -- Small parts ------------------------------------------------------------ */

const pagerStyle = (disabled: boolean): React.CSSProperties => ({
  width: 36, height: 36, borderRadius: 8, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 15, padding: 0,
  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
});
// The shadow is the divider: a border on a sticky cell scrolls away with it
// under border-collapse: separate, a box-shadow stays put.
const stickyCol: React.CSSProperties = { position: "sticky", left: 0, zIndex: 1, background: "var(--surface)", boxShadow: "1px 0 0 var(--line)" };
const th: React.CSSProperties = {
  padding: "9px 10px", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em",
  color: "var(--muted2)", textAlign: "right", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
};
const td: React.CSSProperties = { padding: "9px 10px", borderBottom: "1px solid var(--tint2)", verticalAlign: "middle" };
const numCell: React.CSSProperties = { textAlign: "right", whiteSpace: "nowrap", minWidth: 78 };
/** The annual block starts with a rule, so it reads apart from the month grid. */
const yearCol: React.CSSProperties = { borderLeft: "2px solid var(--line)" };
/** Every column may shrink: fixed tracks overflowed the dialog on a narrow screen. */
const SEG_COLS = "minmax(0, 150px) minmax(0, 1fr) minmax(0, 1fr) 24px";
